<template>
  <el-dialog title="门店二维码" :visible.sync="visible" width="500px" @close="closeDialog">
    <p class="desc">请下载后打印粘贴于门店内，或直接发送给司机。司机微信扫码即可查看并租赁本店车辆。</p>
    <div v-loading="loading" class="qrcode-box">
      <div class="img-box">
        <img v-if="previewImage" :src="previewImage" alt="门店码" class="img" />
      </div>
    </div>
    <div slot="footer">
      <el-button @click="closeDialog">关闭</el-button>
      <el-button @click="downloadQRCode" :disabled="!previewImage" type="primary">下载保存</el-button>
    </div>
  </el-dialog>
</template>
<script setup>
import { ref } from 'vue'
import { getStoreQRCode } from '@/api/domain/shop'
import { trackClick } from '@/utils/trackReport'

const visible = ref(false)
// 二维码图片的base64
const qrCode = ref('')
// 预览图片（canvas生成的）
const previewImage = ref('')
// 门店id
const storeId = ref('')
// 门店名称
const storeName = ref('')
// 获取门店二维码loading
const loading = ref(false)

const showDialog = (row) => {
  const { id, name } = row
  storeId.value = id
  storeName.value = name
  visible.value = true
  getQRCode()
}

const getQRCode = () => {
  loading.value = true
  getStoreQRCode({
    id: storeId.value,
  }).then((res) => {
    const { code, data } = res
    if (code === 1) {
      qrCode.value = data
      if (previewImage.value) {
        URL.revokeObjectURL(previewImage.value)
      }
      previewImage.value = ''
      generatePreview()
      trackClick('preview_qrcode', {
        storeId: storeId.value,
        storeName: storeName.value,
      })
    }
  }).finally(() => {
    loading.value = false
  })
}

// 文字换行处理
const wrapText = (ctx, text, maxWidth) => {
  const lines = []
  let currentLine = ''

  for (let i = 0; i < text.length; i++) {
    const testLine = currentLine + text[i]
    const metrics = ctx.measureText(testLine)

    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine)
      currentLine = text[i]
    } else {
      currentLine = testLine
    }
  }

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines
}

const generatePreview = () => {
  const imgUrl = qrCode.value
  const img = new Image()
  img.onload = () => {
    const canvas = document.createElement('canvas')
    const padding = 20
    const circleRadius = 286
    const naturalWidth = img.naturalWidth || img.width
    const naturalHeight = img.naturalHeight || img.height
    const fontSize = 70
    const textLineHeight = 90
    const textPaddingTop = 20
    const logicalWidth = naturalWidth + padding * 2
    // 先计算门店名称需要几行，再确定canvas高度
    const tempCanvas = document.createElement('canvas')
    const tempCtx = tempCanvas.getContext('2d')
    tempCtx.font = `${fontSize}px sans-serif`
    const maxTextWidth = logicalWidth - padding * 2
    const nameLines = wrapText(tempCtx, storeName.value, maxTextWidth)
    const textHeight = textPaddingTop + nameLines.length * textLineHeight
    const logicalHeight = naturalHeight + padding * 2 + textHeight
    canvas.width = logicalWidth
    canvas.height = logicalHeight
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, logicalWidth, logicalHeight)
    ctx.drawImage(img, padding, padding, naturalWidth, naturalHeight)
    // 绘制圆圈（位于图片正中央）
    const circleCenterX = logicalWidth / 2
    const circleCenterY = padding + naturalHeight / 2
    ctx.beginPath()
    ctx.arc(circleCenterX, circleCenterY, circleRadius, 0, Math.PI * 2)
    ctx.fillStyle = '#ffffff'
    ctx.fill()
    // 圆圈内绘制两行文字：第一行"微信"，第二行"扫码选车"
    ctx.fillStyle = '#000000'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = 'bold 100px sans-serif'
    const lineHeight = 120
    ctx.fillText('微信', circleCenterX, circleCenterY - lineHeight / 2)
    ctx.fillText('扫码选车', circleCenterX, circleCenterY + lineHeight / 2)
    // 图片下方居中显示门店名称（支持换行）
    ctx.fillStyle = '#000000'
    ctx.font = `${fontSize}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    const textStartY = naturalHeight + padding * 2 + textPaddingTop
    nameLines.forEach((line, i) => {
      ctx.fillText(line, logicalWidth / 2, textStartY + i * textLineHeight)
    })

    canvas.toBlob((blob) => {
      if (blob) {
        previewImage.value = URL.createObjectURL(blob)
      }
    }, 'image/png')
  }
  img.src = imgUrl
}

const downloadQRCode = () => {
  trackClick('download_qrcode', {
    storeId: storeId.value,
    storeName: storeName.value,
    imgUrl: previewImage.value,
  })
  if (!previewImage.value) return
  const link = document.createElement('a')
  link.download = `${storeName.value}-门店码.png`
  link.href = previewImage.value
  link.click()
}

const closeDialog = () => {
  visible.value = false
  qrCode.value = ''
  if (previewImage.value) {
    URL.revokeObjectURL(previewImage.value)
  }
  previewImage.value = ''
}
defineExpose({
  showDialog,
})
</script>
<style scoped lang="scss">
.desc {
  margin: 0;
}
.qrcode-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 310px;
  .img-box {
    width: 265px;
    height: 265px;
    position: relative;
    .img {
      width: 100%;
      height: auto;
    }
    .tip-mask {
      position: absolute;
      border-radius: 68px;
      background: #fff;
      width: 136px;
      height: 136px;
      top: 82px;
      left: 82px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-size: 22px;
      font-weight: bold;
    }
  }
  .name {
    font-size: 16px;
  }
}
</style>
